import { useTranslation } from "react-i18next";
import React, {
  useRef,
  forwardRef,
  useImperativeHandle,
  useState,
  useEffect,
} from "react";
import { useLogoByLocale } from "../util/lang";
import { getNavListRq } from "../apis/index.js";
import "./Navbar.scss";

const Navbar = forwardRef((props, ref) => {
  const { t, i18n } = useTranslation();
  // 动态切换LOGO：英文环境用 logoEn，其他用默认 logo
  const currentLogo = useLogoByLocale();
  const [isLangMenuOpen, setIsLangMenuOpen] = useState(false);
  // 新增：控制移动端菜单的显隐状态
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  // 新增：判断当前是否为移动端（根据视窗宽度）
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);
  // 监听语言变化，实时切换LOGO（可选，确保切换语言后LOGO立即更新）
  const [navList, setNavList] = useState([]);
  const menuRefs = useRef({});
  // useEffect(() => {
  //   const fetchNavList = async () => {
  //     try {
  //       const res = await getNavListRq();
  //       if (res && Array.isArray(res)) {
  //         setNavList(res);
  //       }
  //     } catch (err) {
  //       console.log("导航菜单数据请求失败", err);
  //     }
  //   };
  //   fetchNavList();
  // }, [i18n.language]); // ✅ 依赖语言，切换语言自动重新请求对应语言的菜单

  // ✅ 2. 筛选+排序 当前语言的可用导航菜单
  const filterNavList = () => {
    return navList
      .filter((item) => {
        // 过滤条件：匹配当前语言 + 显示 + 启用
        return (
          item.language === i18n.language &&
          item.isShow === 1 &&
          item.isEnabled === 1
        );
      })
      .sort((a, b) => a.sortOrder - b.sortOrder); // 按排序号升序排列
  };
  const currentNavList = filterNavList();
  // // 菜单 refs
  // const menuRefs = {
  //   home: useRef(null),
  //   about: useRef(null),
  //   investment: useRef(null),
  //   csr: useRef(null),
  //   contact: useRef(null),
  // };

  // 暴露 menuRefs 给父组件
  useImperativeHandle(
    ref,
    () => ({
      menuRefs,
    }),
    []
  );

  // 新增：监听视窗变化，更新 isMobile 状态
  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth <= 768);
      // 视窗变大时自动关闭移动端菜单
      if (window.innerWidth > 768) {
        setIsMobileMenuOpen(false);
      }
    };
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  // 点击菜单时的高亮处理函数（新增：关闭移动端菜单）
  const handleMenuClick = (menuKey) => {
    Object.values(menuRefs).forEach((menuRef) => {
      menuRef.current?.classList.remove("active");
    });
    const targetMenu = menuRefs[menuKey]?.current;
    if (targetMenu) {
      targetMenu.classList.add("active");
    }
    setIsLangMenuOpen(false);
    // 点击菜单后关闭移动端菜单
    setIsMobileMenuOpen(false);
  };

  // 切换语言函数
  const handleChangeLang = (lang) => {
    i18n.changeLanguage(lang);
    setIsLangMenuOpen(false);
    setIsMobileMenuOpen(false); // 切换语言后关闭移动端菜单
  };

  return (
    <nav className='navbar'>
      <div className='container navbar-wrapper'>
        {/* 品牌 Logo 区域：使用动态切换的 currentLogo */}
        <div className='navbar-logo'>
          <img src={currentLogo} alt='yield-royal' className='logo' />
        </div>

        {/* 新增：移动端汉堡按钮（仅在移动端显示） */}
        {isMobile && (
          <button
            className='mobile-menu-btn'
            onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
          >
            {isMobileMenuOpen ? (
              <span className='iconfont'>&#xe697;</span>
            ) : (
              <span className='iconfont'>&#xe698;</span>
            )}
          </button>
        )}

        {/* 桌面端导航 + 语言切换（仅在非移动端显示） */}
        <div className={`navbar-right ${isMobile ? "hidden" : ""}`}>
          <div className='navbar-links'>
            {/* ✅ 动态渲染桌面端导航菜单 */}
           
            <a
              ref={menuRefs.home}
              href={`#` + t("navbar.home")}
              className='main-font'
              onClick={() => handleMenuClick("home")}
            >
              {t("navbar.home")}
            </a>
            <a
              ref={menuRefs.about}
              href={`#` + t("navbar.about")}
              className=''
              onClick={() => handleMenuClick("about")}
            >
              {t("navbar.about")}
            </a>
            <a
              ref={menuRefs.investment}
              href={`#` + t("navbar.investment")}
              className=''
              onClick={() => handleMenuClick("investment")}
            >
              {t("navbar.investment")}
            </a>
            <a
              ref={menuRefs.csr}
              href={`#` + t("navbar.csr")}
              className=''
              onClick={() => handleMenuClick("csr")}
            >
              {t("navbar.csr")}
            </a>
            <a
              ref={menuRefs.contact}
              href={`#` + t("navbar.contact")}
              className=''
              onClick={() => handleMenuClick("contact")}
            >
              {t("navbar.contact")}
            </a>
			
          </div>

          <div className='lang-switch-container'>
            <button
              className='lang-switch-btn'
              onClick={() => setIsLangMenuOpen(!isLangMenuOpen)}
            >
              <span className='lang-icon iconfont'>&#xe696;</span>
            </button>
            {isLangMenuOpen && (
              <div className='lang-dropdown'>
                <div
                  className='lang-option'
                  onClick={() => handleChangeLang("en")}
                >
                  EN
                </div>
                <div
                  className='lang-option'
                  onClick={() => handleChangeLang("zh-TW")}
                >
                  繁中
                </div>
                <div
                  className='lang-option'
                  onClick={() => handleChangeLang("zh")}
                >
                  简中
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* 新增：移动端菜单弹窗（仅在移动端 + 菜单打开时显示） */}
      {isMobile && isMobileMenuOpen && (
        <div className='mobile-menu-overlay'>
          <div className='mobile-menu-content'>
            {/* 移动端导航链接 */}
            <div className='mobile-navbar-links'>
              {/* ✅ 动态渲染桌面端导航菜单 */}
              {currentNavList.map((item) => (
                <a
                  key={item.id}
                  ref={(el) => (menuRefs.current[item.name] = el)}
                  href={`#` + item.name}
                  className=''
                  onClick={() => handleMenuClick(item.name)}
                >
                  {item.name}
                </a>
              ))}
              {/* <a
                ref={menuRefs.home}
                href={`#` + t("navbar.home")}
                className=''
                onClick={() => handleMenuClick("home")}
              >
                {t("navbar.home")}
              </a>
              <a
                ref={menuRefs.about}
                href={`#` + t("navbar.about")}
                className=''
                onClick={() => handleMenuClick("about")}
              >
                {t("navbar.about")}
              </a>
              <a
                ref={menuRefs.investment}
                href={`#` + t("navbar.investment")}
                className=''
                onClick={() => handleMenuClick("investment")}
              >
                {t("navbar.investment")}
              </a>
              <a
                ref={menuRefs.csr}
                href={`#` + t("navbar.csr")}
                className=''
                onClick={() => handleMenuClick("csr")}
              >
                {t("navbar.csr")}
              </a>
              <a
                ref={menuRefs.contact}
                href={`#` + t("navbar.contact")}
                className=''
                onClick={() => handleMenuClick("contact")}
              >
                {t("navbar.contact")}
              </a> */}
            </div>

            {/* 移动端语言切换 */}
            <div className='mobile-lang-switch'>
              <div className='mobile-lang-options'>
                <div className='mobile-lang-title'>
                  <span className='lang-icon iconfont'>&#xe696;</span>
                </div>
                <div
                  className='mobile-lang-option'
                  onClick={() => handleChangeLang("en")}
                >
                  EN
                </div>
                <div
                  className='mobile-lang-option'
                  onClick={() => handleChangeLang("zh-TW")}
                >
                  繁中
                </div>
                <div
                  className='mobile-lang-option'
                  onClick={() => handleChangeLang("zh")}
                >
                  简中
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </nav>
  );
});

export default Navbar;
